/**
 * Enhanced Logging System for MfersArcade
 * Based on shottracker's logger implementation
 * Provides local console logging with optional remote WebSocket streaming
 */

interface LogData {
  level: string;
  message: string;
  data: any;
  category: string;
  timestamp: string;
  userAgent: string;
  url: string;
  viewport: {
    width: number;
    height: number;
  };
}

interface LoggerStatus {
  isProduction: boolean;
  enhancedLogging: boolean;
  remoteLoggingEnabled: boolean;
  connected: boolean;
  wsUrl: string | null;
  queueSize: number;
  reconnectAttempts: number;
  shouldReconnect: boolean;
  currentLevel: string;
  isEnabled: boolean;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Check if we're in production mode
function checkIsProduction(): boolean {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.hostname === 'mfersarcade.com' || 
               window.location.hostname === 'mfersarcade.net' ||
               window.location.hostname === 'arcade.mfers.com';
    }
    if (typeof process !== 'undefined' && process.env) {
        return process.env.NODE_ENV === 'production';
    }
    return false;
}

class Logger {
    private isProduction: boolean;
    private isEnhancedLogging: boolean;
    private wsUrl: string | null;
    private ws: WebSocket | null;
    private messageQueue: LogData[];
    private connected: boolean;
    private reconnectAttempts: number;
    private maxReconnectAttempts: number;
    private reconnectDelay: number;
    private maxReconnectDelay: number;
    private backoffMultiplier: number;
    private shouldReconnect: boolean;
    private lastMessageTime: number;
    private messageRateLimit: number;
    private remoteLoggingEnabled: boolean;
    private maxQueueSize: number;
    private queueFlushInterval: number;
    private queueTimer: ReturnType<typeof setInterval> | null;
    private currentLevel: LogLevel;
    private isEnabled: boolean;
    private LEVELS: Record<LogLevel, number>;

    constructor() {
        this.isProduction = checkIsProduction();
        this.isEnhancedLogging = this.checkEnhancedLogging();
        this.wsUrl = this.getWebSocketUrl();
        this.ws = null;
        this.messageQueue = [];
        this.connected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.backoffMultiplier = 2;
        this.shouldReconnect = true;
        this.lastMessageTime = 0;
        this.messageRateLimit = 100;
        this.remoteLoggingEnabled = false;
        this.maxQueueSize = 200;
        this.queueFlushInterval = 5000;
        this.queueTimer = null;
        
        // Log levels
        this.LEVELS = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3
        };
        
        // Set default log level based on environment
        this.currentLevel = this.isProduction ? 'error' : 'debug';
        this.isEnabled = !this.isProduction || this.isDebugMode();
        
        // Only attempt remote logging if both enhanced logging is enabled AND we have a URL
        if (this.isEnhancedLogging && !this.isProduction) {
            if (this.wsUrl) {
                this.remoteLoggingEnabled = true;
                this.initializeWebSocket();
                this.startQueueTimer();
            } else {
                console.info('🔍 Enhanced logging enabled but no WebSocket URL configured. Using console-only logging.');
            }
        }
        
        this.setupGlobalErrorHandling();
    }
    
    /**
     * Check if enhanced logging is enabled via query parameter
     */
    private checkEnhancedLogging(): boolean {
        if (typeof window === 'undefined' || !window.location) {
            return false;
        }
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('enhancedLogging');
    }
    
    /**
     * Check if debug mode is enabled via URL parameter or localStorage
     */
    isDebugMode() {
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('debug') === 'true') {
                return true;
            }
        }

        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('debug') === 'true';
        }

        return false;
    }
    
    /**
     * Get WebSocket URL from URL params, environment, or build config
     */
    getWebSocketUrl() {
        if (typeof window === 'undefined' || !window.location) {
            return null;
        }
        
        const urlParams = new URLSearchParams(window.location.search);
        
        // Priority 1: Runtime URL parameter
        const runtimeWsUrl = urlParams.get('wsUrl');
        if (runtimeWsUrl && runtimeWsUrl.trim()) {
            const cleanUrl = runtimeWsUrl.trim();
            
            if (cleanUrl.startsWith('ws://') || cleanUrl.startsWith('wss://')) {
                console.info(`🔗 Using runtime WebSocket URL: ${cleanUrl}`);
                return cleanUrl;
            } else {
                console.warn(`⚠️ Invalid WebSocket URL format: ${cleanUrl} (must start with ws:// or wss://)`);
            }
        }
        
        // Priority 2: Environment variable
        if (process.env.REACT_APP_WS_URL && process.env.REACT_APP_WS_URL.trim()) {
            const envUrl = process.env.REACT_APP_WS_URL.trim();
            console.info(`🔗 Using environment WebSocket URL: ${envUrl}`);
            return envUrl;
        }
        
        // Priority 3: Localhost fallback for development
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            const localUrl = 'ws://localhost:8080';
            console.info(`🔗 Using localhost WebSocket URL: ${localUrl}`);
            return localUrl;
        }
        
        return null;
    }
    
    /**
     * Initialize WebSocket connection
     */
    initializeWebSocket() {
        if (!this.wsUrl || !this.shouldReconnect || this.isProduction) return;
        
        try {
            this.ws = typeof window !== 'undefined' && window.WebSocket ? new window.WebSocket(this.wsUrl) : null;
            
            if (!this.ws) {
                throw new Error('WebSocket not available');
            }
            
            this.ws.onopen = () => {
                this.connected = true;
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                this.log('info', '🔗 Connected to remote logging server', { url: this.wsUrl });
                this.flushMessageQueue();
            };
            
            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'welcome') {
                        this.log('info', `📡 ${data.message}`);
                    }
                } catch (e) {
                    // Ignore malformed messages
                }
            };
            
            this.ws.onclose = () => {
                this.connected = false;
                
                if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.scheduleReconnect();
                } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    console.warn('🚫 Max reconnection attempts reached. Switching to console-only logging.');
                    this.shouldReconnect = false;
                    this.remoteLoggingEnabled = false;
                    this.stopQueueTimer();
                    this.messageQueue = [];
                }
            };
            
            this.ws.onerror = () => {
                this.connected = false;
            };
            
        } catch (error) {
            this.connected = false;
            this.log('warn', `Failed to connect to logging server: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    
    /**
     * Schedule reconnection with exponential backoff
     */
    scheduleReconnect() {
        this.reconnectAttempts++;
        
        setTimeout(() => {
            if (this.shouldReconnect) {
                this.log('info', `🔄 Reconnecting to logging server (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                this.initializeWebSocket();
            }
        }, this.reconnectDelay);
        
        this.reconnectDelay = Math.min(
            this.reconnectDelay * this.backoffMultiplier,
            this.maxReconnectDelay
        );
    }
    
    /**
     * Flush queued messages when connection is established
     */
    flushMessageQueue(): void {
        while (this.messageQueue.length > 0 && this.connected) {
            const message = this.messageQueue.shift();
            if (message) {
                this.sendToRemote(message);
            }
        }
    }
    
    /**
     * Send message to remote server
     */
    sendToRemote(logData: LogData): boolean {
        if (!this.isEnhancedLogging || !this.ws || (typeof window !== 'undefined' && window.WebSocket && this.ws.readyState !== window.WebSocket.OPEN) || this.isProduction) {
            return false;
        }
        
        const now = Date.now();
        if (now - this.lastMessageTime < this.messageRateLimit) {
            return false;
        }
        this.lastMessageTime = now;
        
        try {
            this.ws.send(JSON.stringify(logData));
            return true;
        } catch (error) {
            this.connected = false;
            return false;
        }
    }
    
    /**
     * Queue message for later sending
     */
    queueMessage(logData: LogData): void {
        if (!this.remoteLoggingEnabled || this.isProduction) {
            return;
        }
        
        while (this.messageQueue.length >= this.maxQueueSize) {
            this.messageQueue.shift();
        }
        
        this.messageQueue.push(logData);
    }
    
    /**
     * Start periodic queue flushing timer
     */
    startQueueTimer() {
        if (!this.remoteLoggingEnabled || this.isProduction) return;
        
        this.queueTimer = setInterval(() => {
            if (this.messageQueue.length > 0 && !this.connected && this.shouldReconnect) {
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.initializeWebSocket();
                }
            }
        }, this.queueFlushInterval);
    }
    
    /**
     * Stop queue timer
     */
    stopQueueTimer() {
        if (this.queueTimer) {
            clearInterval(this.queueTimer);
            this.queueTimer = null;
        }
    }
    
    /**
     * Check if logging should occur for given level
     */
    shouldLog(level: LogLevel): boolean {
        if (this.isDebugMode()) {
            return true;
        }

        if (this.isProduction && !this.isEnabled) {
            return level === 'error';
        }

        const currentLevelValue = this.LEVELS[this.currentLevel] || 0;
        const requestedLevelValue = this.LEVELS[level] || 0;
        
        return requestedLevelValue >= currentLevelValue;
    }
    
    /**
     * Core logging method
     */
    log(level: LogLevel, message: string, data: unknown = null, category: string = 'general'): void {
        if (!this.shouldLog(level)) {
            return;
        }
        
        const timestamp = new Date().toLocaleTimeString();
        const consoleMessage = `[${timestamp}] ${message}`;
        
        switch (level) {
            case 'error':
                console.error(consoleMessage, data || '');
                break;
            case 'warn':
                console.warn(consoleMessage, data || '');
                break;
            case 'info':
                console.info(consoleMessage, data || '');
                break;
            default:
                console.log(consoleMessage, data || '');
        }
        
        if (this.remoteLoggingEnabled && !this.isProduction) {
            const logData = {
                level,
                message,
                data,
                category,
                timestamp: new Date().toISOString(),
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                url: typeof window !== 'undefined' && window.location ? window.location.href : 'unknown',
                viewport: {
                    width: typeof window !== 'undefined' ? window.innerWidth : 0,
                    height: typeof window !== 'undefined' ? window.innerHeight : 0
                }
            };
            
            if (!this.sendToRemote(logData)) {
                this.queueMessage(logData);
            }
        }
    }
    
    /**
     * Convenience methods
     */
    info(message: string, data: unknown = null, category: string = 'general'): void {
        this.log('info', message, data, category);
    }

    warn(message: string, data: unknown = null, category: string = 'general'): void {
        this.log('warn', message, data, category);
    }

    error(message: string, data: unknown = null, category: string = 'general'): void {
        this.log('error', message, data, category);
    }

    debug(message: string, data: unknown = null, category: string = 'general'): void {
        this.log('debug', message, data, category);
    }
    
    /**
     * Specialized logging methods for different categories
     */
    game(eventType: string, data: unknown = null): void {
        this.log('info', `Game: ${eventType}`, data, 'game');
    }

    ui(message: string, data: unknown = null): void {
        this.log('info', `UI: ${message}`, data, 'ui');
    }

    analytics(event: string, data: unknown = null): void {
        this.log('info', `Analytics: ${event}`, data, 'analytics');
    }

    network(event: string, data: unknown = null): void {
        this.log('info', `Network: ${event}`, data, 'network');
    }
    
    /**
     * Setup global error handling
     */
    setupGlobalErrorHandling() {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            return;
        }
        
        window.addEventListener('error', (event) => {
            this.error('Unhandled Error', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            }, 'error');
        });
        
        window.addEventListener('unhandledrejection', (event) => {
            this.error('Unhandled Promise Rejection', {
                reason: event.reason?.toString(),
                stack: event.reason?.stack
            }, 'error');
        });
    }
    
    /**
     * Performance logging
     */
    time(label: string): void {
        console.time(label);
        this.debug(`Timer started: ${label}`, null, 'performance');
    }

    timeEnd(label: string): void {
        console.timeEnd(label);
        this.debug(`Timer ended: ${label}`, null, 'performance');
    }

    /**
     * Measure performance of an operation
     */
    async measure<T>(label: string, operation: () => Promise<T>): Promise<T> {
        const start = typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now();
        this.debug(`Starting: ${label}`, null, 'performance');
        
        try {
            const result = await operation();
            const duration = typeof window !== 'undefined' && window.performance ? window.performance.now() - start : Date.now() - start;
            this.debug(`Completed: ${label}`, { duration: `${duration.toFixed(2)}ms` }, 'performance');
            return result;
        } catch (error) {
            const duration = typeof window !== 'undefined' && window.performance ? window.performance.now() - start : Date.now() - start;
            this.error(`Failed: ${label}`, {
                duration: `${duration.toFixed(2)}ms`,
                error: error instanceof Error ? error.message : String(error)
            }, 'performance');
            throw error;
        }
    }
    
    /**
     * Enable debug logging
     */
    enableDebug() {
        this.currentLevel = 'debug';
        this.isEnabled = true;
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('debug', 'true');
        }
    }
    
    /**
     * Disable debug logging
     */
    disableDebug() {
        this.currentLevel = this.isProduction ? 'error' : 'debug';
        this.isEnabled = !this.isProduction;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('debug');
        }
    }
    
    /**
     * Set the current log level
     */
    setLevel(level: LogLevel): void {
        if (Object.prototype.hasOwnProperty.call(this.LEVELS, level)) {
            this.currentLevel = level;
        }
    }

    /**
     * Enable or disable logging
     */
    setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
    }
    
    /**
     * Clean shutdown
     */
    destroy() {
        this.shouldReconnect = false;
        this.remoteLoggingEnabled = false;
        this.stopQueueTimer();
        
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        
        this.messageQueue = [];
    }
    
    /**
     * Get current logging status for debugging
     */
    getStatus() {
        return {
            isProduction: this.isProduction,
            enhancedLogging: this.isEnhancedLogging,
            remoteLoggingEnabled: this.remoteLoggingEnabled,
            connected: this.connected,
            wsUrl: this.wsUrl,
            queueSize: this.messageQueue.length,
            reconnectAttempts: this.reconnectAttempts,
            shouldReconnect: this.shouldReconnect,
            currentLevel: this.currentLevel,
            isEnabled: this.isEnabled
        };
    }
    
    /**
     * Helper method to generate debug URL for sharing
     */
    generateDebugUrl(baseUrl = typeof window !== 'undefined' && window.location ? window.location.origin + window.location.pathname : '') {
        if (!this.wsUrl) {
            return `${baseUrl}?enhancedLogging&wsUrl=YOUR_NGROK_URL_HERE`;
        }
        
        const url = new URL(baseUrl);
        url.searchParams.set('enhancedLogging', '');
        url.searchParams.set('wsUrl', this.wsUrl);
        return url.toString();
    }
}

// Create singleton instance
const logger = new Logger();

// Export both the instance and the class
export { Logger };
export default logger;